import { Box, Typography, Paper } from '@gapartners/ui';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { TalkTimeBreakdownDTO } from '@/types/qualityMetrics';

interface TalkTimeChartProps {
  data: TalkTimeBreakdownDTO;
  title?: string;
  height?: number;
  totalTalkTimeSeconds?: number;
}

export function TalkTimeChart({
  data,
  title = 'Talk Time Distribution',
  height = 300,
  totalTalkTimeSeconds = 0,
}: TalkTimeChartProps) {
  const { t } = useTranslation();
  const chartData = [
    {
      name: t('pages.manager.qualityMetrics.call.components.customer'),
      value: data.customerPercentage,
      fill: '#2196F3',
    },
    {
      name: t('pages.manager.qualityMetrics.call.components.agent'),
      value: data.agentPercentage,
      fill: '#1565C0',
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, height }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {data.customerPercentage > 0 || data.agentPercentage > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={height - 120}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            >
              <XAxis dataKey="name" />
              <YAxis
                label={{
                  value: t(
                    'pages.manager.qualityMetrics.call.components.percentageLabel'
                  ),
                  angle: -90,
                  position: 'insideLeft',
                }}
                domain={[0, 100]}
              />
              <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {totalTalkTimeSeconds > 0 && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'pages.manager.qualityMetrics.call.components.totalTalkTime'
                )}{' '}
                {Math.floor(totalTalkTimeSeconds / 60)}m{' '}
                {totalTalkTimeSeconds % 60}s
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={height - 60}
        >
          <Typography color="text.secondary">
            {t('pages.manager.qualityMetrics.call.components.noData')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
